/**
 * Snusify hero: the slide carousel and the quick-find panel.
 *
 * Both are progressive. Without this script the first slide shows, the rest
 * stay hidden, and the quick-find link goes straight to its collection. With
 * it, the link becomes a toggle for the filter form beside it.
 *
 * The carousel never auto-advances, so it needs no pause control.
 */

const SWIPE_THRESHOLD = 48;

/** The intro's length once started, matching the longest delay plus duration in the stylesheets. */
const INTRO_MS = 2600;
/** Longest the intro waits for the visible tiles' images before it starts anyway. */
const INTRO_WAIT_MS = 1200;
/** Tiles stagger in groups of this size, so a long row does not trail on. */
const INTRO_STAGGER = 9;
/** A change of slide's copy coming in (.is-slide-in in the stylesheet), its last part included. */
const SLIDE_IN_MS = 1600;

class SnusifyHero extends HTMLElement {
  #index = 0;
  #slideInTimer = 0;
  #slides = [];
  #dots = [];
  #live = null;
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused, and listeners bound to it would silently never fire. */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#slides = [...this.querySelectorAll('[data-slide]')];
    this.#dots = [...this.querySelectorAll('[data-hero-dot]')];
    this.#live = this.querySelector('[data-hero-live]');

    this.#initVideo(signal);
    this.#initCarousel(signal);
    this.#initQuickFind(signal);
    this.#initSpots(signal);
    this.#initIntro(signal);
  }

  /**
   * The backdrop video's source, chosen here rather than in the markup. A
   * <source media=""> is ignored inside a <video>, and a video left to
   * autoplay fetches whatever it was given even where the stylesheet hides
   * it, so the markup cannot pick for itself: a phone was paying for the
   * full-size file and seeing none of it. The element arrives empty at
   * preload="none"; this hands it the file the screen calls for, and hands
   * it nothing where motion is turned down - the backdrop picture, which
   * lies underneath, is what shows then, and while the file loads.
   * @param {AbortSignal} signal
   */
  #initVideo(signal) {
    const video = /** @type {HTMLVideoElement | null} */ (this.querySelector('[data-hero-video]'));
    if (!video) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const wide = matchMedia('(min-width: 990px)');
    const load = () => {
      const src = wide.matches ? video.dataset.src : video.dataset.srcSmall;
      if (!src || video.getAttribute('src') === src) return;
      video.setAttribute('src', src);
      video.load();
      // Autoplay is refused where the visitor has not interacted with the
      // page yet on some phones; the picture underneath carries it then.
      video.play().catch(() => {});
    };

    // The phone's file is a derived address, so it can miss. The full file
    // is always a real one: fall back to it rather than show nothing.
    video.addEventListener(
      'error',
      () => {
        const full = video.dataset.src;
        if (!full || video.getAttribute('src') === full) return;
        video.setAttribute('src', full);
        video.load();
        video.play().catch(() => {});
      },
      { signal }
    );

    load();
    wide.addEventListener('change', load, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * Product spots on the picture. A pointer opens a spot's card by hovering
   * (the stylesheet does that); this adds the tap and the keyboard: the
   * button toggles its card, one card at a time, and Escape or a press
   * anywhere else closes it.
   * @param {AbortSignal} signal
   */
  #initSpots(signal) {
    const spots = [...this.querySelectorAll('[data-spot]')];
    if (spots.length === 0) return;

    const setOpen = (spot, isOpen) => {
      spot.classList.toggle('is-open', isOpen);
      spot.querySelector('[data-spot-toggle]')?.setAttribute('aria-expanded', String(isOpen));
    };

    for (const spot of spots) {
      const toggle = spot.querySelector('[data-spot-toggle]');

      toggle?.addEventListener(
        'click',
        () => {
          const isOpen = !spot.classList.contains('is-open');
          for (const other of spots) {
            if (other !== spot) setOpen(other, false);
          }
          setOpen(spot, isOpen);
        },
        { signal }
      );

      spot.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !spot.classList.contains('is-open')) return;
          setOpen(spot, false);
          toggle?.focus();
        },
        { signal }
      );
    }

    document.addEventListener(
      'pointerdown',
      (event) => {
        for (const spot of spots) {
          if (!spot.contains(event.target)) setOpen(spot, false);
        }
      },
      { signal }
    );

    // A demonstration button (snippets/snusify-quick-add) adds nothing; it
    // only shows the tick a real add would.
    for (const button of this.querySelectorAll('[data-demo-add]')) {
      button.addEventListener(
        'click',
        () => {
          button.classList.add('is-added');
          window.setTimeout(() => button.classList.remove('is-added'), 1600);
        },
        { signal }
      );
    }
  }

  /** @param {AbortSignal} signal */
  #initCarousel(signal) {
    if (this.#slides.length < 2) return;

    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.show(this.#index - 1), { signal });
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.show(this.#index + 1), { signal });

    for (const [i, dot] of this.#dots.entries()) {
      dot.addEventListener('click', () => this.show(i), { signal });
    }

    this.querySelector('[data-hero-controls]')?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.#index - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.#index + 1);
        }
      },
      { signal }
    );

    this.#initSwipe(signal);
  }

  /** Horizontal swipe on the frame, for touch and pen. */
  #initSwipe(signal) {
    const frame = this.querySelector('.snusify-hero__frame');
    if (!frame) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    frame.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse') return;
        if (event.target.closest('button, a, input, label, .snusify-qf')) return;
        tracking = true;
        startX = event.clientX;
        startY = event.clientY;
      },
      { signal }
    );

    frame.addEventListener(
      'pointerup',
      (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        this.show(dx < 0 ? this.#index + 1 : this.#index - 1);
      },
      { signal }
    );

    frame.addEventListener('pointercancel', () => (tracking = false), { signal });
  }

  /**
   * @param {number} n - target slide; wraps in both directions
   */
  show(n) {
    const count = this.#slides.length;
    const from = this.#index;
    this.#index = ((n % count) + count) % count;

    for (const [i, slide] of this.#slides.entries()) {
      slide.hidden = i !== this.#index;
    }

    for (const [i, dot] of this.#dots.entries()) {
      const active = i === this.#index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }

    // A slide's own deal button stands in for the quick find button, and each
    // slide brings its own second button along with it.
    const slide = this.#slides[this.#index];
    for (const button of this.querySelectorAll('[data-slide-cta]')) {
      button.hidden = button.dataset.slideCta !== String(this.#index);
    }
    for (const button of this.querySelectorAll('[data-slide-cta2]')) {
      button.hidden = button.dataset.slideCta2 !== String(this.#index);
    }
    for (const stamp of this.querySelectorAll('[data-slide-stamp]')) {
      stamp.hidden = stamp.dataset.slideStamp !== String(this.#index);
    }
    const finder = this.querySelector('[data-qf-open]');
    if (finder) finder.hidden = slide.hasAttribute('data-own-cta');

    if (this.#index !== from) this.#slideIn();
    this.#announce(slide.getAttribute('aria-label') ?? '');
  }

  /**
   * Plays the landing page intro's copy again for the slide just shown: its
   * eyebrow, the heading's words, its text and the buttons come in one
   * after another (.is-slide-in in the stylesheet). From the start on every
   * change, however quick; the panel and the picture, the same on every
   * slide, stay as they are.
   */
  #slideIn() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(this.#slideInTimer);
    this.classList.remove('is-slide-in');
    // A style read between the two, or the animations would not start over.
    void this.offsetWidth;
    this.classList.add('is-slide-in');
    this.#slideInTimer = window.setTimeout(() => this.classList.remove('is-slide-in'), SLIDE_IN_MS);
  }

  /** Clear-then-set, so a repeated message is still announced. */
  #announce(message) {
    if (!this.#live) return;
    this.#live.textContent = '';
    requestAnimationFrame(() => {
      this.#live.textContent = message;
    });
  }

  /**
   * Runs the landing page intro when the header's inline script has chosen to
   * play it (.snusify-intro on the root). It starts once the eagerly loaded
   * tiles, or the background image, have their images, or after INTRO_WAIT_MS, and when it has played
   * both classes are removed so no blur layer stays behind on the tiles.
   * @param {AbortSignal} signal
   */
  #initIntro(signal) {
    const root = document.documentElement;
    if (!root.classList.contains('snusify-intro') || root.classList.contains('snusify-intro-ready')) return;

    for (const [i, tile] of this.querySelectorAll('.snusify-marquee__tile').entries()) {
      tile.style.setProperty('--snusify-i', String(i % INTRO_STAGGER));
    }

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      root.classList.add('snusify-intro-ready');
      window.setTimeout(() => root.classList.remove('snusify-intro', 'snusify-intro-ready'), INTRO_MS);
    };

    const pending = [
      ...this.querySelectorAll('.snusify-marquee__tile img[loading="eager"], .snusify-hero__backdrop > img'),
    ].filter(
      (img) => !img.complete
    );
    if (pending.length === 0) {
      start();
      return;
    }

    let left = pending.length;
    const settle = () => {
      left -= 1;
      if (left <= 0) start();
    };
    for (const img of pending) {
      img.addEventListener('load', settle, { once: true, signal });
      img.addEventListener('error', settle, { once: true, signal });
    }
    window.setTimeout(start, INTRO_WAIT_MS);
  }

  /** @param {AbortSignal} signal */
  #initQuickFind(signal) {
    const open = this.querySelector('[data-qf-open]');
    const panel = this.querySelector('[data-qf-panel]');
    const cta = this.querySelector('[data-qf-cta]');
    const close = this.querySelector('[data-qf-close]');
    const count = this.querySelector('[data-qf-count]');
    if (!open || !panel || !cta || !close) return;

    // Upgrade the plain link into a disclosure button.
    open.setAttribute('role', 'button');
    open.setAttribute('aria-expanded', 'false');
    open.setAttribute('aria-controls', open.dataset.controls);

    const setOpen = (isOpen) => {
      cta.hidden = isOpen;
      panel.hidden = !isOpen;
      open.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) {
        panel.querySelector('input, [data-qf-link]')?.focus({ preventScroll: true });
      } else {
        // The slide may have changed while the panel was open, hiding this button.
        (open.hidden ? cta.querySelector('a:not([hidden])') : open)?.focus({ preventScroll: true });
      }
    };

    open.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        setOpen(true);
      },
      { signal }
    );

    open.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== ' ') return;
        event.preventDefault();
        setOpen(true);
      },
      { signal }
    );

    close.addEventListener('click', () => setOpen(false), { signal });

    panel.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') setOpen(false);
      },
      { signal }
    );

    // A strength link carries whatever filters are chosen, so the two kinds
    // of step combine; the submit button shows how many filters are chosen.
    const links = [...panel.querySelectorAll('[data-qf-link]')];
    for (const link of links) link.dataset.base = link.getAttribute('href') ?? '';

    panel.addEventListener(
      'change',
      () => {
        const params = new URLSearchParams(new FormData(panel)).toString();
        for (const link of links) {
          link.setAttribute('href', params ? `${link.dataset.base}?${params}` : link.dataset.base);
        }

        if (!count) return;
        const chosen = panel.querySelectorAll('input:checked').length;
        count.hidden = chosen === 0;
        count.textContent = String(chosen);
      },
      { signal }
    );
  }
}

if (!customElements.get('snusify-hero')) {
  customElements.define('snusify-hero', SnusifyHero);
}
